import React, { useState, useEffect, useCallback, useContext } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Table,
  Button,
  Form,
  InputGroup,
  Card,
  Badge,
  Modal,
  Tab,
  Tabs,
  Alert,
  Container,
  Row,
  Col,
  Spinner,
  ButtonGroup,
} from "react-bootstrap";
import {
  FaEdit,
  FaTrash,
  FaEye,
  FaSearch,
  FaShare,
  FaStar,
  FaTimes,
  FaPrint,
} from "react-icons/fa";
import api, {
  getFlaggedSystemQuestions,
  getFlaggedStatus,
  toggleFlaggedStatus,
  getAllFlaggedStatuses,
} from "../api";
import MathJaxRenderer from "./MathJaxRenderer";
import { AuthContext } from "../contexts/AuthContext";
import { FixedSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";

/**
 * Virtualized table row component for efficient rendering
 * @param {Object} props - Component props
 * @param {Object} props.data - The data for all rows
 * @param {number} props.index - Current row index
 * @param {Object} props.style - Style object from react-window
 */
const VirtualizedRow = ({ data, index, style }) => {
  const {
    items,
    onViewClick,
    onToggleFlag,
    onSelectToggle,
    selectedIds,
    isSelected,
    isFlagging,
    handleDelete,
  } = data;
  const question = items[index];

  if (!question) return null;

  // Determine the correct question ID to use - system questions use system_id, user questions use id
  const questionId =
    question.source === "system" ? question.system_id : question.id;
  const questionType = question.source || "user";

  const isQuestionSelected = isSelected(question);

  return (
    <div
      style={{
        ...style,
        display: "flex",
        alignItems: "center",
        borderBottom: "1px solid #dee2e6",
        backgroundColor: isQuestionSelected
          ? "#e7f1ff"
          : index % 2 === 0
          ? "#f8f9fa"
          : "white",
        cursor: "pointer",
        transition: "background-color 0.15s ease-in-out",
      }}
      onClick={() => onSelectToggle(question)}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = isQuestionSelected
          ? "#d4e5ff"
          : "#f0f0f0";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = isQuestionSelected
          ? "#e7f1ff"
          : index % 2 === 0
          ? "#f8f9fa"
          : "white";
      }}
    >
      <div style={{ flex: "0 0 50px", textAlign: "center" }}>
        <Form.Check
          type="checkbox"
          checked={isQuestionSelected}
          onChange={(e) => {
            e.stopPropagation();
            onSelectToggle(question);
          }}
        />
      </div>
      <div style={{ flex: "0 0 50px", textAlign: "center" }}>
        <Button
          variant="link"
          className="p-0 text-warning"
          onClick={(e) => {
            e.stopPropagation();
            onToggleFlag(question);
          }}
          disabled={isFlagging(questionId)}
        >
          {isFlagging(questionId) ? (
            <Spinner size="sm" animation="border" />
          ) : (
            <FaStar
              className={question.flagged ? "text-warning" : "text-muted"}
              style={{ fontSize: "1.2rem" }}
            />
          )}
        </Button>
      </div>
      <div
        style={{
          flex: "1 1 auto",
          padding: "0 10px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {question.title || "Untitled"}
      </div>
      <div style={{ flex: "0 0 150px", padding: "0 10px" }}>
        {question.subject || "-"}
      </div>
      <div style={{ flex: "0 0 80px", padding: "0 10px" }}>
        {question.level || "-"}
      </div>
      <div style={{ flex: "0 0 120px", padding: "0 10px" }}>
        {question.created_at
          ? new Date(question.created_at).toLocaleDateString()
          : "-"}
      </div>
      <div
        style={{
          flex: "0 0 180px",
          padding: "0 10px",
          display: "flex",
          gap: "5px",
          justifyContent: "center",
        }}
      >
        <Button
          variant="outline-primary"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onViewClick(question);
          }}
        >
          <FaEye /> View
        </Button>
        {questionType !== "system" && (
          <Button
            variant="outline-danger"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(question);
            }}
          >
            <FaTrash /> Delete
          </Button>
        )}
      </div>
    </div>
  );
};

// Add a pagination component to improve performance with large datasets
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;

  const pageNumbers = [];
  const maxPageButtons = 5;

  // Calculate the range of page numbers to display
  let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
  let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);

  // Adjust the start page if we're near the end
  if (endPage - startPage + 1 < maxPageButtons) {
    startPage = Math.max(1, endPage - maxPageButtons + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  return (
    <nav>
      <ul className="pagination">
        {currentPage > 1 && (
          <li className="page-item">
            <Button
              variant="link"
              className="page-link"
              onClick={() => onPageChange(currentPage - 1)}
            >
              &laquo;
            </Button>
          </li>
        )}

        {startPage > 1 && (
          <>
            <li className="page-item">
              <Button
                variant="link"
                className="page-link"
                onClick={() => onPageChange(1)}
              >
                1
              </Button>
            </li>
            {startPage > 2 && (
              <li className="page-item disabled">
                <span className="page-link">...</span>
              </li>
            )}
          </>
        )}

        {pageNumbers.map((number) => (
          <li
            key={number}
            className={`page-item ${currentPage === number ? "active" : ""}`}
          >
            <Button
              variant={currentPage === number ? "primary" : "link"}
              className="page-link"
              onClick={() => onPageChange(number)}
            >
              {number}
            </Button>
          </li>
        ))}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && (
              <li className="page-item disabled">
                <span className="page-link">...</span>
              </li>
            )}
            <li className="page-item">
              <Button
                variant="link"
                className="page-link"
                onClick={() => onPageChange(totalPages)}
              >
                {totalPages}
              </Button>
            </li>
          </>
        )}

        {currentPage < totalPages && (
          <li className="page-item">
            <Button
              variant="link"
              className="page-link"
              onClick={() => onPageChange(currentPage + 1)}
            >
              &raquo;
            </Button>
          </li>
        )}
      </ul>
    </nav>
  );
};

const MyQuestions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { auth } = useContext(AuthContext);
  const [questions, setQuestions] = useState([]);
  const [flaggedSystemQuestions, setFlaggedSystemQuestions] = useState([]);
  const [sharedQuestions, setSharedQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [subjectFilter, setSubjectFilter] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [sharing, setSharing] = useState(false);
  const [viewFilter, setViewFilter] = useState("all"); // all, user, flagged

  // View question modal
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  // Delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Share modal
  const [showShareModal, setShowShareModal] = useState(false);

  // Flagging state
  const [flaggedQuestions, setFlaggedQuestions] = useState({});
  const [flaggingInProgress, setFlaggingInProgress] = useState({});

  // New state for question selection
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [selectMode, setSelectMode] = useState(false);

  // Add pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Add data chunking for better rendering performance
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [questionChunks, setQuestionChunks] = useState([]);
  const [displayedQuestions, setDisplayedQuestions] = useState([]);

  // Define fetchAllData for clean data fetching
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch user questions - simple and direct
      let userQuestions = [];
      try {
        const userQuestionsResponse = await api.get("/api/user-questions/");

        // Process the user questions response
        if (userQuestionsResponse && userQuestionsResponse.data) {
          if (Array.isArray(userQuestionsResponse.data)) {
            userQuestions = userQuestionsResponse.data;
          } else if (
            userQuestionsResponse.data.results &&
            Array.isArray(userQuestionsResponse.data.results)
          ) {
            userQuestions = userQuestionsResponse.data.results;
          }

          // Add source field to each question
          userQuestions = userQuestions.map((q) => ({
            ...q,
            source: "user",
          }));
        }
      } catch (error) {
        console.error("Error fetching user questions:", error);
        userQuestions = [];
      }

      // Fetch all flag statuses in one API call for efficiency
      try {
        const flagStatusesResponse = await getAllFlaggedStatuses();
        console.log("Retrieved all flag statuses:", flagStatusesResponse);

        // Update state with all flag statuses
        setFlaggedQuestions(flagStatusesResponse);

        // Apply flag status to user questions based on the response
        userQuestions = userQuestions.map((q) => ({
          ...q,
          flagged: !!flagStatusesResponse[`${q.id}`],
        }));

        console.log(
          `User questions with flagged=true after API check: ${
            userQuestions.filter((q) => q.flagged === true).length
          }`
        );
      } catch (error) {
        console.error("Error fetching flag statuses:", error);
        // Continue with unflagged questions
      }

      // Set user questions state
      setQuestions(userQuestions);

      // Fetch flagged system questions using our new simplified method
      try {
        const systemFlagged = await getFlaggedSystemQuestions();

        // Process the response to match our expected format
        const formattedSystemQuestions = systemFlagged.map((q) => ({
          id: `system-${q.system_id}`,
          system_id: q.system_id,
          title: q.title || `System Question ${q.system_id}`,
          subject: q.subject || "-",
          level: q.level || "-",
          created_at: q.created_at || new Date().toISOString(),
          source: "system",
          flagged: true, // If it came from the flagged endpoint, it's flagged
          question_body:
            q.question_body || "<p>Question content will load when viewed</p>",
          markscheme_body:
            q.markscheme_body || "<p>Markscheme will load when viewed</p>",
        }));

        console.log(
          `Retrieved ${formattedSystemQuestions.length} flagged system questions from API`
        );
        setFlaggedSystemQuestions(formattedSystemQuestions);
      } catch (error) {
        console.error("Error fetching flagged system questions:", error);
        setFlaggedSystemQuestions([]);
      }

      // Log current state of flagged content
      console.log("User questions flagged status:", flaggedQuestions);
      console.log("System flagged questions:", flaggedSystemQuestions.length);
    } catch (err) {
      console.error("Error in fetchAllData:", err);
      setError("Failed to load your questions. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Add function to toggle flag status using our new API method
  const handleToggleFlag = useCallback(
    async (question) => {
      const questionId =
        question.source === "system" ? question.system_id : question.id;
      const questionType = question.source === "system" ? "system" : "user";

      console.log(`Toggling flag for ${questionType} question ${questionId}`);

      // Set loading state
      setFlaggingInProgress((prev) => ({
        ...prev,
        [questionId]: true,
      }));

      try {
        // Determine current flag state and prepare for optimistic update
        const currentFlagState = question.flagged === true;
        const newFlagState = !currentFlagState;

        // Optimistically update UI
        if (questionType === "user") {
          // Update user question in the list
          setQuestions((prevQuestions) =>
            prevQuestions.map((q) =>
              q.id === questionId ? { ...q, flagged: newFlagState } : q
            )
          );

          // Update flagged questions state
          setFlaggedQuestions((prev) => ({
            ...prev,
            [questionId]: newFlagState,
          }));
        } else {
          // Handle system questions
          if (newFlagState) {
            // Add to flagged system questions if not already there
            const exists = flaggedSystemQuestions.some(
              (q) => q.system_id === questionId
            );
            if (!exists) {
              const newQuestion = {
                id: `system-${questionId}`,
                system_id: questionId,
                title: question.title || `System Question ${questionId}`,
                subject: question.subject || "-",
                level: question.level || "-",
                created_at: new Date().toISOString(),
                source: "system",
                flagged: true,
                question_body:
                  question.question_body ||
                  "<p>Question content will load when viewed</p>",
                markscheme_body:
                  question.markscheme_body ||
                  "<p>Markscheme will load when viewed</p>",
              };

              setFlaggedSystemQuestions((prev) => [...prev, newQuestion]);
            }
          } else {
            // Remove from flagged system questions
            setFlaggedSystemQuestions((prev) =>
              prev.filter((q) => q.system_id !== questionId)
            );
          }
        }

        // Send API request
        const newFlagStatus = await toggleFlaggedStatus(
          questionType,
          questionId
        );

        // If the server response differs from our optimistic update, sync with server
        if (newFlagStatus !== newFlagState) {
          console.log(
            `Server returned different flag state: ${newFlagStatus}, updating UI`
          );
          if (questionType === "user") {
            setQuestions((prevQuestions) =>
              prevQuestions.map((q) =>
                q.id === questionId ? { ...q, flagged: newFlagStatus } : q
              )
            );
            setFlaggedQuestions((prev) => ({
              ...prev,
              [questionId]: newFlagStatus,
            }));
          } else {
            if (newFlagStatus) {
              // Should be added to flagged
              const exists = flaggedSystemQuestions.some(
                (q) => q.system_id === questionId
              );
              if (!exists) {
                // Add it
                const newQuestion = {
                  id: `system-${questionId}`,
                  system_id: questionId,
                  title: question.title || `System Question ${questionId}`,
                  subject: question.subject || "-",
                  level: question.level || "-",
                  created_at: new Date().toISOString(),
                  source: "system",
                  flagged: true,
                  question_body:
                    question.question_body ||
                    "<p>Question content will load when viewed</p>",
                  markscheme_body:
                    question.markscheme_body ||
                    "<p>Markscheme will load when viewed</p>",
                };
                setFlaggedSystemQuestions((prev) => [...prev, newQuestion]);
              }
            } else {
              // Should be removed
              setFlaggedSystemQuestions((prev) =>
                prev.filter((q) => q.system_id !== questionId)
              );
            }
          }
        }

        // Update the global flag status
        setFlaggedQuestions((prev) => ({
          ...prev,
          [`${questionType === "system" ? "system_" : ""}${questionId}`]:
            newFlagStatus,
        }));
      } catch (error) {
        console.error("Error toggling flag:", error);
        // Revert optimistic update if needed
        if (questionType === "user") {
          setQuestions((prevQuestions) =>
            prevQuestions.map((q) =>
              q.id === questionId ? { ...q, flagged: question.flagged } : q
            )
          );
        } else {
          // No need to revert system questions as they'll be refetched on refresh
        }
      } finally {
        // Clear loading state
        setFlaggingInProgress((prev) => ({
          ...prev,
          [questionId]: false,
        }));
      }
    },
    [flaggedSystemQuestions]
  );

  // Simplified function to fetch flagged system questions
  const fetchFlaggedSystemQuestions = async () => {
    try {
      console.log("Fetching flagged system questions directly from API");
      const systemQuestions = await getFlaggedSystemQuestions();

      console.log(
        `Retrieved ${systemQuestions.length} flagged system questions from API`
      );

      // Format the system questions
      const formattedQuestions = systemQuestions.map((q) => ({
        id: `system-${q.system_id}`,
        system_id: q.system_id,
        title: q.title || `System Question ${q.system_id}`,
        subject: q.subject || "-",
        level: q.level || "-",
        created_at: q.created_at || new Date().toISOString(),
        source: "system",
        flagged: true,
        question_body:
          q.question_body || "<p>Question content will load when viewed</p>",
        markscheme_body:
          q.markscheme_body || "<p>Markscheme will load when viewed</p>",
      }));

      setFlaggedSystemQuestions(formattedQuestions);
      console.log(
        `Final flagged system questions count: ${formattedQuestions.length}`
      );
    } catch (error) {
      console.error("Error fetching flagged system questions:", error);
      setFlaggedSystemQuestions([]);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    // Set initial view based on location state
    if (location.state && location.state.viewFilter) {
      console.log(
        "Setting initial view filter from location state:",
        location.state.viewFilter
      );
      setViewFilter(location.state.viewFilter);
    } else {
      console.log("Using default view filter: all");
      // Default to showing flagged questions if no specific view is requested
      setViewFilter("flagged");
    }

    // Log the initial component state
    console.log("MyQuestions component initial state:", {
      loading,
      error,
      questions: questions.length,
      viewFilter,
      flaggedSystemQuestions: flaggedSystemQuestions.length,
    });

    // Immediate data load
    fetchAllData();
    fetchSubjects();

    // Only fetch users if user is admin
    if (auth && auth.isAdmin) {
      fetchUsers();
    } else {
      // Set empty users array for non-admins
      setUsers([]);
    }

    // Add a small delay and then fetch again to ensure we have the latest data
    // This helps ensure components have time to initialize and any cached data is refreshed
    const refreshTimerId = setTimeout(() => {
      console.log("Running delayed flagged questions refresh");
      fetchFlaggedSystemQuestions();
    }, 800);

    // Clean up timer
    return () => clearTimeout(refreshTimerId);
  }, [fetchAllData, auth, location.state]);

  // Add this effect specifically for viewFilter changes
  useEffect(() => {
    console.log("View filter changed to:", viewFilter);

    // When switching to flagged view, refresh the data
    if (viewFilter === "flagged") {
      console.log("Refreshing flagged questions data for flagged view");
      fetchFlaggedSystemQuestions();
    }
  }, [viewFilter]);

  // Add function to check flag status for questions
  const checkQuestionFlagStatuses = useCallback(async (questionsList) => {
    if (!questionsList || questionsList.length === 0) return;

    try {
      const statusChecks = {};

      for (const question of questionsList) {
        if (question.source === "user") {
          // Use the API function to check flag status
          const flagged = await getFlaggedStatus("user", question.id);
          statusChecks[question.id] = flagged;
        }
      }

      setFlaggedQuestions((prev) => ({
        ...prev,
        ...statusChecks,
      }));
    } catch (err) {
      console.error("Error checking flag statuses:", err);
    }
  }, []);

  const fetchSubjects = async () => {
    try {
      const response = await api.get("/api/csv-data/subjects/");
      console.log("Subjects response:", response.data);

      // Handle different response formats
      if (Array.isArray(response.data)) {
        // If it's an array of objects with id and name
        if (
          response.data.length > 0 &&
          response.data[0].id &&
          response.data[0].name
        ) {
          setSubjects(response.data);
        }
        // If it's an array of strings
        else {
          setSubjects(
            response.data.map((item, index) => {
              if (typeof item === "object" && item !== null) {
                return item;
              } else if (typeof item === "string") {
                return { id: index, name: item };
              } else {
                return { id: index, name: String(item) };
              }
            })
          );
        }
      } else {
        console.error("Unexpected subjects data format:", response.data);
        setSubjects([]);
      }
    } catch (error) {
      console.error("Error fetching subjects:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      // Only attempt to fetch users if the user is an admin
      const response = await api.get("/api/users/");
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      // Set empty users array on error
      setUsers([]);
    }
  };

  const handleViewQuestion = async (question) => {
    if (!question || (!question.id && !question.system_id)) {
      console.error("Attempted to view an invalid question:", question);
      setError("Cannot view this question. Invalid data.");
      return;
    }

    // Log the question data to console for debugging
    console.log("Viewing question:", question);

    // Check if this is a system question with missing content
    let questionData = { ...question };

    // For system questions with no content, try to fetch it directly
    if (
      question.source === "system" &&
      question.system_id &&
      (!question.question_body ||
        !question.markscheme_body ||
        question.question_body.includes("content is not available"))
    ) {
      try {
        console.log(
          "Attempting to fetch system question content:",
          question.system_id
        );
        // Set loading state or indicator if needed

        // Try to fetch the question data directly from the API
        const response = await api.get(`/api/csv-data/${question.system_id}/`);

        if (response && response.data) {
          console.log("Fetched system question:", response.data);

          // Update with the fetched content
          questionData = {
            ...questionData,
            title: response.data.title || questionData.title,
            subject: response.data.subject || questionData.subject,
            level: response.data.level || questionData.level,
            marks: response.data.marks || questionData.marks,
            question_body:
              response.data.question_body || questionData.question_body,
            markscheme_body:
              response.data.markscheme_body || questionData.markscheme_body,
          };
        }
      } catch (error) {
        console.warn("Could not fetch system question details:", error);
        // Keep using the placeholder content
      }
    }

    // Make sure all required fields are present
    questionData = {
      ...questionData,
      title: questionData.title || "Untitled Question",
      subject: questionData.subject || "-",
      level: questionData.level || "-",
      marks: questionData.marks || "-",
      question_body: questionData.question_body || "",
      markscheme_body: questionData.markscheme_body || "",
    };

    setSelectedQuestion(questionData);
    setShowViewModal(true);
  };

  // New function to print the currently viewed question
  const handlePrintCurrentQuestion = () => {
    if (!selectedQuestion) return;

    console.log("Printing current question:", selectedQuestion);

    // Navigate to worksheet generator with only this question - use /worksheets path to match route in App.js
    navigate("/worksheets", {
      state: {
        selectedQuestions: [selectedQuestion],
        fromMyQuestions: true,
      },
    });
  };

  const handleShareQuestion = (question) => {
    // Prevent sharing system questions
    if (question.source === "system") {
      setError("System questions cannot be shared directly.");
      return;
    }

    // Check if user is admin
    if (!auth || !auth.isAdmin) {
      setError("Only administrators can share questions.");
      return;
    }

    if (!question || !question.id) {
      console.error("Attempted to share an invalid question:", question);
      setError("Cannot share this question. Invalid data.");
      return;
    }
    setSelectedQuestion(question);
    setSelectedUsers([]);
    setShowShareModal(true);
  };

  const handleDeleteQuestion = (question) => {
    // Prevent deleting system questions
    if (question.source === "system") {
      setError("System questions cannot be deleted.");
      return;
    }

    if (!question || !question.id) {
      console.error("Attempted to delete an invalid question:", question);
      setError("Cannot delete this question. Invalid data.");
      return;
    }
    setSelectedQuestion(question);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedQuestion) return;

    try {
      await api.delete(`/api/user-questions/${selectedQuestion.id}/`);
      setQuestions(questions.filter((q) => q.id !== selectedQuestion.id));
      setShowDeleteModal(false);
      setSelectedQuestion(null);
    } catch (error) {
      console.error("Error deleting question:", error);
      setError("Error deleting question. Please try again.");
    }
  };

  const shareQuestion = async () => {
    if (!selectedQuestion || !selectedQuestion.id) {
      setError("Invalid question selected for sharing.");
      return;
    }

    if (selectedUsers.length === 0) {
      setError("Please select at least one user to share with.");
      return;
    }

    setSharing(true);

    try {
      await api.post(`/api/question-shares/`, {
        question: selectedQuestion.id,
        shared_with: selectedUsers,
      });

      setShowShareModal(false);
      setError(null);
    } catch (error) {
      console.error("Error sharing question:", error);
      const errorMessage =
        error.response?.data?.error || error.message || "Unknown error";
      setError(`Failed to share question: ${errorMessage}`);
    } finally {
      setSharing(false);
    }
  };

  const handleUserSelect = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter((id) => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  // Get all questions based on the current view filter
  const getAllQuestions = () => {
    // Add debug logging to understand what's happening
    console.log("getAllQuestions called with viewFilter:", viewFilter);
    console.log("User questions count:", questions.length);
    console.log(
      "User questions with flagged=true:",
      questions.filter((q) => q.flagged === true).length
    );
    console.log(
      "Flagged system questions count:",
      flaggedSystemQuestions.length
    );

    // For each filter type, return the appropriate questions
    if (viewFilter === "all") {
      // Return all questions + flagged system questions
      const allQuestions = [...questions, ...flaggedSystemQuestions];
      console.log(`Total questions (all view): ${allQuestions.length}`);
      return allQuestions;
    } else if (viewFilter === "flagged") {
      // Get user questions that are flagged using both the question's flagged property
      // and the flaggedQuestions state object
      const userFlagged = questions.filter((q) => {
        const questionId = q.id.toString();
        return q.flagged === true || flaggedQuestions[questionId] === true;
      });

      console.log(
        `Filtered user questions with flag=true: ${userFlagged.length}`
      );

      // Combine user flagged questions with system flagged questions
      const allFlagged = [...userFlagged, ...flaggedSystemQuestions];
      console.log(`Total flagged questions: ${allFlagged.length}`);
      return allFlagged;
    } else if (viewFilter === "shared") {
      // Only return shared questions
      return sharedQuestions || [];
    } else if (viewFilter === "user") {
      // Only user's created questions
      return questions;
    } else {
      // Default: return all user questions but no system questions
      return questions;
    }
  };

  // Filter questions based on search term and subject filter
  const filteredQuestions = () => {
    const allQuestions = getAllQuestions();
    console.log("Total questions before filtering:", allQuestions?.length || 0);

    if (!allQuestions || allQuestions.length === 0) {
      return [];
    }

    const filtered = allQuestions.filter((question) => {
      const matchesSearch = searchTerm
        ? (question.title || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (question.subject || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (question.level || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        : true;

      const matchesSubject = subjectFilter
        ? (question.subject || "").toLowerCase() === subjectFilter.toLowerCase()
        : true;

      return matchesSearch && matchesSubject;
    });

    console.log("Questions after filtering:", filtered.length);
    return filtered;
  };

  // Simplify the toggleQuestionSelection function
  const toggleQuestionSelection = (question) => {
    const questionId = question?.id || question?.system_id;
    const questionSource = question?.source || "user";

    if (selectedQuestionIds.some((q) => q.id === questionId)) {
      // Remove from selected
      setSelectedQuestionIds(
        selectedQuestionIds.filter((q) => q.id !== questionId)
      );
    } else {
      // Add to selected
      setSelectedQuestionIds([
        ...selectedQuestionIds,
        {
          id: questionId,
          source: questionSource,
          question: question,
        },
      ]);
    }
  };

  // Handle printing selected questions - ensure we're passing full question objects
  const handlePrintSelected = () => {
    if (selectedQuestionIds.length === 0) {
      setError("Please select at least one question to print");
      return;
    }

    // Get the full question objects for the selected IDs
    const selectedQuestions = getAllQuestions().filter((question) => {
      const questionId = question?.id || question?.system_id;
      return selectedQuestionIds.some((q) => q.id === questionId);
    });

    // Log the selected questions for debugging
    console.log("Selected questions to print:", selectedQuestions);

    if (selectedQuestions.length === 0) {
      console.error(
        "Failed to find matching question objects for the selected IDs"
      );
      setError("Failed to prepare selected questions. Please try again.");
      return;
    }

    // Navigate to worksheet generator with selected questions - use /worksheets path to match route in App.js
    navigate("/worksheets", {
      state: {
        selectedQuestions: selectedQuestions,
        fromMyQuestions: true,
      },
    });
  };

  // Toggle select mode
  const toggleSelectMode = () => {
    const newSelectMode = !selectMode;
    setSelectMode(newSelectMode);

    // If turning off select mode, clear selections
    if (!newSelectMode) {
      setSelectedQuestionIds([]);
    }
  };

  // Replace the existing fetchQuestions function with this optimized version
  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch user's own questions with pagination
      const userResponse = await api.get("/api/user-questions/", {
        params: {
          page: currentPage,
          page_size: pageSize,
        },
      });

      let userQuestions = [];
      let totalCount = 0;

      if (userResponse.data) {
        if (Array.isArray(userResponse.data)) {
          userQuestions = userResponse.data;
          totalCount = userQuestions.length;
        } else if (
          userResponse.data.results &&
          Array.isArray(userResponse.data.results)
        ) {
          userQuestions = userResponse.data.results;
          totalCount = userResponse.data.count || userQuestions.length;
        }
      }

      // Mark each question's source
      userQuestions = userQuestions.map((q) => ({
        ...q,
        source: "user",
        flagged: false, // Will be updated by flag check
      }));

      // Update pagination info
      setTotalItems(totalCount);
      setTotalPages(Math.ceil(totalCount / pageSize));

      // Process in chunks for better performance
      const CHUNK_SIZE = 10; // Process flags in batches
      const chunks = [];

      for (let i = 0; i < userQuestions.length; i += CHUNK_SIZE) {
        chunks.push(userQuestions.slice(i, i + CHUNK_SIZE));
      }

      setQuestionChunks(chunks);
      setQuestions(userQuestions);

      // Initial display - show all questions but without flag status yet
      setDisplayedQuestions(userQuestions);

      // Start loading flag statuses in the background
      processQuestionChunks(chunks, userQuestions);

      // Also fetch flagged system questions (if appropriate)
      if (viewFilter !== "user") {
        fetchFlaggedSystemQuestions();
      }
    } catch (error) {
      console.error("Error fetching questions:", error);
      setError("Failed to load questions. Please try again.");
    } finally {
      // Main loading is done when questions are displayed, even if flags are still loading
      setLoading(false);
    }
  }, [currentPage, pageSize, viewFilter]);

  // Add a function to process question chunks to avoid UI freezing
  const processQuestionChunks = async (chunks, allQuestions) => {
    if (!chunks || chunks.length === 0) return;

    // Process each chunk with a small delay to keep UI responsive
    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];

      // Process this chunk
      const updatedChunk = await processChunkFlagStatus(chunk);

      // Update the full questions array with the processed chunk
      const startIndex = i * 10; // Based on CHUNK_SIZE
      const updatedQuestions = [...allQuestions];

      updatedChunk.forEach((question, idx) => {
        updatedQuestions[startIndex + idx] = question;
      });

      // Update the state with the progress so far
      setQuestions(updatedQuestions);
      setDisplayedQuestions(updatedQuestions);

      // Small delay to keep UI responsive between chunks
      if (i < chunks.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
    }
  };

  // Process flag status for a chunk of questions
  const processChunkFlagStatus = async (chunk) => {
    const updatedChunk = [...chunk];

    // Process flag checks in parallel for this chunk
    const flagChecks = chunk.map(async (question, index) => {
      try {
        // Use the checkFlagStatus utility
        const questionType = question.source;
        const questionId = question.id;

        const isFlagged = await getFlaggedStatus(questionType, questionId);
        updatedChunk[index] = {
          ...updatedChunk[index],
          flagged: isFlagged,
        };
      } catch (err) {
        console.warn(`Error checking flag for question ${question.id}:`, err);
      }
    });

    await Promise.all(flagChecks);
    return updatedChunk;
  };

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Re-fetch when pagination changes
  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions, currentPage, pageSize]);

  // Update the render method for the question list to use virtualization
  const renderQuestionList = () => {
    const allQuestions = getAllQuestions();
    console.log(`renderQuestionList with ${allQuestions.length} questions`);

    // If there are no questions
    if (allQuestions.length === 0) {
      if (loading) {
        return (
          <div className="text-center my-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-3">Loading your questions...</p>
          </div>
        );
      }

      return (
        <div className="text-center my-5">
          <p className="fs-5">
            {viewFilter === "flagged"
              ? "You don't have any flagged questions yet."
              : viewFilter === "shared"
              ? "You don't have any shared questions yet."
              : "You don't have any questions yet."}
          </p>
          <Button
            variant="primary"
            onClick={() => navigate("/upload-question")}
            className="mt-3"
          >
            Upload a New Question
          </Button>
        </div>
      );
    }

    // Debug: Verify what we're displaying
    console.log(
      `Displaying ${allQuestions.length} questions:`,
      allQuestions.slice(0, 2)
    );

    return (
      <div
        style={{
          height: "600px",
          border: "1px solid #dee2e6",
          borderRadius: "4px",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            padding: "10px",
            background: "#f8f9fa",
            borderBottom: "2px solid #dee2e6",
            fontWeight: "bold",
          }}
        >
          <div style={{ flex: "0 0 50px", textAlign: "center" }}></div>
          <div style={{ flex: "0 0 50px", textAlign: "center" }}></div>
          <div style={{ flex: "1 1 auto", padding: "0 10px" }}>Title</div>
          <div style={{ flex: "0 0 150px", padding: "0 10px" }}>Subject</div>
          <div style={{ flex: "0 0 80px", padding: "0 10px" }}>Level</div>
          <div style={{ flex: "0 0 120px", padding: "0 10px" }}>Date</div>
          <div
            style={{
              flex: "0 0 180px",
              padding: "0 10px",
              textAlign: "center",
            }}
          >
            Actions
          </div>
        </div>

        {/* Virtualized List */}
        <div style={{ height: "calc(100% - 43px)" }}>
          <AutoSizer>
            {({ height, width }) => (
              <List
                height={height}
                itemCount={allQuestions.length}
                itemSize={60}
                width={width}
                itemData={{
                  items: allQuestions,
                  onViewClick: handleViewQuestion,
                  onToggleFlag: handleToggleFlag,
                  onSelectToggle: toggleQuestionSelection,
                  selectedIds: selectedQuestionIds,
                  isSelected: (q) =>
                    selectedQuestionIds.some(
                      (s) => s.id === (q.id || q.system_id)
                    ),
                  isFlagging: (id) => flaggingInProgress[id],
                  handleDelete: handleDeleteQuestion,
                }}
              >
                {VirtualizedRow}
              </List>
            )}
          </AutoSizer>
        </div>
      </div>
    );
  };

  return (
    <Container className="my-4">
      <h2 className="mb-4">My Questions</h2>

      <Row className="mb-4">
        <Col md={6}>
          <InputGroup>
            <Form.Control
              placeholder="Search questions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <Button
                variant="outline-secondary"
                onClick={() => setSearchTerm("")}
              >
                <FaTimes />
              </Button>
            )}
            <Button variant="outline-primary" disabled>
              <FaSearch />
            </Button>
          </InputGroup>
        </Col>
        <Col md={4}>
          <Form.Select
            value={subjectFilter}
            onChange={(e) => setSubjectFilter(e.target.value)}
          >
            <option value="">All Subjects</option>
            {Array.isArray(subjects) ? (
              subjects.map((subject, index) => (
                <option key={index} value={subject.name || subject}>
                  {subject.name || subject}
                </option>
              ))
            ) : (
              <option disabled>Error loading subjects</option>
            )}
          </Form.Select>
        </Col>
        <Col md={2} className="d-flex justify-content-end">
          <Button
            variant="primary"
            onClick={() => navigate("/upload-question")}
          >
            New Question
          </Button>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={9}>
          <ButtonGroup>
            <Button
              variant={viewFilter === "all" ? "primary" : "outline-primary"}
              onClick={() => setViewFilter("all")}
            >
              All Questions
            </Button>
            <Button
              variant={viewFilter === "user" ? "primary" : "outline-primary"}
              onClick={() => setViewFilter("user")}
            >
              My Created Questions
            </Button>
            <Button
              variant={viewFilter === "flagged" ? "primary" : "outline-primary"}
              onClick={() => setViewFilter("flagged")}
            >
              Flagged Questions
            </Button>
          </ButtonGroup>
        </Col>
        <Col md={3} className="d-flex justify-content-end">
          <ButtonGroup>
            {!selectMode ? (
              <Button
                variant="outline-primary"
                onClick={toggleSelectMode}
                title="Select multiple questions to create a worksheet"
              >
                <FaPrint className="me-1" /> Create Worksheet
              </Button>
            ) : (
              <>
                <Button
                  variant="success"
                  onClick={handlePrintSelected}
                  disabled={selectedQuestionIds.length === 0}
                >
                  <FaPrint className="me-1" />
                  Create Worksheet ({selectedQuestionIds.length})
                </Button>
                <Button variant="outline-secondary" onClick={toggleSelectMode}>
                  Cancel
                </Button>
              </>
            )}
          </ButtonGroup>
        </Col>
      </Row>

      {/* Add a selection info banner when in select mode */}
      {selectMode && (
        <Alert
          variant="info"
          className="mb-3 d-flex justify-content-between align-items-center"
        >
          <div>
            <strong>Worksheet Creation Mode</strong> - Select questions to
            include in your worksheet.
            {selectedQuestionIds.length > 0 && (
              <span className="ms-2">
                Selected: {selectedQuestionIds.length} question
                {selectedQuestionIds.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>
          <div>
            <Button
              variant="link"
              className="text-decoration-none p-0 ms-3"
              onClick={() => setSelectedQuestionIds([])}
              disabled={selectedQuestionIds.length === 0}
            >
              Clear Selection
            </Button>
          </div>
        </Alert>
      )}

      {renderQuestionList()}

      <Modal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        size="xl"
        dialogClassName="question-preview-modal"
        style={{ maxWidth: "95%", margin: "10px auto" }}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedQuestion?.title || "Question Details"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ minHeight: "500px", padding: "20px" }}>
          {/* Add MathJax script to ensure math rendering */}
          <script
            type="text/javascript"
            id="MathJax-script"
            async
            src="https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-mml-chtml.js"
          ></script>

          <div className="question-details">
            <Row className="mb-3">
              <Col md={4}>
                <strong>Subject:</strong> {selectedQuestion?.subject || "-"}
              </Col>
              <Col md={4}>
                <strong>Level:</strong> {selectedQuestion?.level || "-"}
              </Col>
              <Col md={4}>
                <strong>Marks:</strong> {selectedQuestion?.marks || "-"}
              </Col>
            </Row>

            <Tabs defaultActiveKey="question" className="mb-3">
              <Tab eventKey="question" title="Question">
                {selectedQuestion?.question_body ? (
                  <div
                    className="p-3 border rounded bg-light question-content"
                    style={{
                      minHeight: "200px",
                      maxHeight: "500px",
                      overflowY: "auto",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: selectedQuestion.question_body,
                    }}
                  />
                ) : (
                  <p className="text-muted">No question content available.</p>
                )}
              </Tab>
              <Tab eventKey="markscheme" title="Markscheme">
                {selectedQuestion?.markscheme_body ? (
                  <div
                    className="p-3 border rounded bg-light markscheme-content"
                    style={{
                      minHeight: "200px",
                      maxHeight: "500px",
                      overflowY: "auto",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: selectedQuestion.markscheme_body,
                    }}
                  />
                ) : (
                  <p className="text-muted">No markscheme available.</p>
                )}
              </Tab>
            </Tabs>
          </div>

          {/* Custom styles for modal content */}
          <style
            dangerouslySetInnerHTML={{
              __html: `
                .question-content img, .markscheme-content img {
                  max-width: 100%;
                  height: auto;
                }
                .question-content table, .markscheme-content table {
                  border-collapse: collapse;
                  width: 100%;
                  margin-bottom: 1rem;
                }
                .question-content table, .question-content th, .question-content td,
                .markscheme-content table, .markscheme-content th, .markscheme-content td {
                  border: 1px solid #ddd;
                  padding: 8px;
                }
                /* Fix modal display */
                .modal-dialog.question-preview-modal {
                  margin: 0.5rem auto;
                  max-width: 90% !important;
                }
                @media (min-width: 992px) {
                  .modal-dialog.question-preview-modal {
                    max-width: 85% !important;
                  }
                }
                /* Fix mathjax rendering */
                .MathJax {
                  display: inline !important;
                }
              `,
            }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            Close
          </Button>
          <Button variant="success" onClick={handlePrintCurrentQuestion}>
            <FaPrint className="me-1" /> Print Worksheet
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this question?
          <div className="mt-2 p-2 bg-light rounded">
            <strong>Title:</strong> {selectedQuestion?.title || "Untitled"}
            <br />
            <strong>Subject:</strong> {selectedQuestion?.subject || "-"}
          </div>
          <div className="text-danger mt-3">This action cannot be undone.</div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showShareModal} onHide={() => setShowShareModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Share Question</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          <p>
            Share <strong>{selectedQuestion?.title || "this question"}</strong>{" "}
            with:
          </p>

          <Form.Group className="mb-3">
            <div
              className="user-list border rounded p-2"
              style={{ maxHeight: "200px", overflowY: "auto" }}
            >
              {users && users.length === 0 ? (
                <p className="text-muted m-2">No users available.</p>
              ) : Array.isArray(users) ? (
                users.map((user) => (
                  <Form.Check
                    key={user.id}
                    type="checkbox"
                    id={`user-${user.id}`}
                    label={`${user.username} (${user.email || "No email"})`}
                    checked={selectedUsers.includes(user.id)}
                    onChange={() => handleUserSelect(user.id)}
                    className="m-2"
                  />
                ))
              ) : (
                <p className="text-muted m-2">Error loading users</p>
              )}
            </div>
            <Form.Text className="text-muted">
              Select one or more users to share this question with.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowShareModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={shareQuestion}
            disabled={sharing || selectedUsers.length === 0}
          >
            {sharing ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Sharing...
              </>
            ) : (
              "Share"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default MyQuestions;
